import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';

interface AdDetail {
  id: number;
  title: string;
  description: string;
  price: number;
  price_type: string;
  quantity: number;
  weight: number | null;
  age_months: number | null;
  gender: string;
  status: string;
  contact_phone: string;
  show_phone: boolean;
  views_count: number;
  favorites_count: number;
  is_promoted: boolean;
  latitude: number | null;
  longitude: number | null;
  address: string | null;
  created_at: string;
  published_at: string;
  expires_at: string;
  category: { id: number; name_ar: string; slug: string } | null;
  subcategory: { id: number; name_ar: string } | null;
  age_category: { id: number; name_ar: string } | null;
  city: { id: number; name_ar: string } | null;
  user: {
    id: number;
    name: string;
    avatar: string | null;
    is_verified: boolean;
    account_type: string;
    rating_avg: string;
    city: string;
    created_at: string;
  } | null;
  images: { id: number; image_url: string; thumbnail_url: string | null; is_primary: boolean; sort_order: number }[];
  tags: { id: number; name_ar: string }[];
}

interface Comment {
  id: number;
  body: string;
  user: { id: number; name: string; avatar: string | null; is_verified: boolean } | null;
  is_owner: boolean;
  parent_id: number | null;
  created_at: string;
  replies: Comment[];
}

interface SimilarAd {
  id: number;
  title: string;
  price: number;
  city: { name_ar: string } | null;
  category: { name_ar: string } | null;
  images: { image_url: string }[];
}

const AdDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [ad, setAd] = useState<AdDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [showPhone, setShowPhone] = useState(false);
  const [imageZoom, setImageZoom] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentsTotal, setCommentsTotal] = useState(0);
  const [commentBody, setCommentBody] = useState('');
  const [replyTo, setReplyTo] = useState<{ id: number; name: string } | null>(null);
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [similarAds, setSimilarAds] = useState<SimilarAd[]>([]);

  useEffect(() => {
    const fetchAd = async () => {
      try {
        setLoading(true);
        const res = await api.get('/ads/' + id);
        setAd(res.data.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAd();
    fetchComments();
    fetchSimilar();
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchComments = async () => {
    try {
      setCommentLoading(true);
      const res = await api.get('/ads/' + id + '/comments');
      setComments(res.data.data.comments || []);
      setCommentsTotal(res.data.data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setCommentLoading(false);
    }
  };

  const fetchSimilar = async () => {
    try {
      const res = await api.get('/ads?limit=3&exclude=' + id);
      setSimilarAds(res.data.data?.data || res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const submitComment = async () => {
    if (!commentBody.trim()) return;
    try {
      setCommentSubmitting(true);
      const payload: any = { body: commentBody };
      if (replyTo) payload.parent_id = replyTo.id;
      const res = await api.post('/ads/' + id + '/comments', payload);
      if (res.data.success) {
        setCommentBody('');
        setReplyTo(null);
        fetchComments();
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        window.alert('سجّل دخولك أولاً');
      }
    } finally {
      setCommentSubmitting(false);
    }
  };

  const deleteComment = async (commentId: number) => {
    if (!window.confirm('حذف التعليق؟')) return;
    try {
      await api.delete('/ads/' + id + '/comments/' + commentId);
      fetchComments();
    } catch (err) {
      console.error(err);
    }
  };

  const timeAgo = (dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diff < 60) return 'الآن';
    if (diff < 3600) return 'قبل ' + Math.floor(diff / 60) + ' دقيقة';
    if (diff < 86400) return 'قبل ' + Math.floor(diff / 3600) + ' ساعة';
    if (diff < 2592000) return 'قبل ' + Math.floor(diff / 86400) + ' يوم';
    return 'قبل ' + Math.floor(diff / 2592000) + ' شهر';
  };

  const memberSince = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const months = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];
    return 'عضو منذ ' + months[d.getMonth()] + ' ' + d.getFullYear();
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 900, margin: '25px auto', padding: '0 20px', fontFamily: "'Cairo', sans-serif" }}>
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E0E0E0', padding: 40 }}>
          <div style={{ height: 20, background: '#F5F5F5', borderRadius: 6, width: '40%', marginBottom: 15 }}></div>
          <div style={{ height: 30, background: '#F5F5F5', borderRadius: 6, width: '70%', marginBottom: 15 }}></div>
          <div style={{ height: 200, background: '#F5F5F5', borderRadius: 12 }}></div>
        </div>
      </div>
    );
  }

  if (!ad) {
    return (
      <div style={{ maxWidth: 900, margin: '60px auto', textAlign: 'center', fontFamily: "'Cairo', sans-serif" }}>
        <p style={{ color: '#9E9E9E', fontSize: 18, marginBottom: 10 }}>الإعلان غير موجود</p>
        <Link to="/ads" style={{ color: '#1976D2', fontSize: 14 }}>العودة للإعلانات</Link>
      </div>
    );
  }

  const genderMap: Record<string, string> = { male: 'ذكر', female: 'أنثى', mixed: 'مختلط' };
  const genderLabel = genderMap[ad.gender] || ad.gender;
  const priceMap: Record<string, string> = { fixed: 'ثابت', negotiable: 'قابل للتفاوض', auction: 'مزاد' };
  const priceTypeLabel = priceMap[ad.price_type] || '';

  const specs = [
    { label: 'التصنيف', value: ad.category?.name_ar, icon: '⭐' },
    { label: 'السلالة', value: ad.subcategory?.name_ar, icon: '🐑' },
    { label: 'فئة العمر', value: ad.age_category?.name_ar, icon: '📅' },
    { label: 'الجنس', value: genderLabel, icon: '♂️' },
    { label: 'الكمية', value: ad.quantity ? ad.quantity + ' رأس' : null, icon: '📊' },
    { label: 'الوزن', value: ad.weight ? ad.weight + ' كجم' : null, icon: '⚖️' },
    { label: 'العمر', value: ad.age_months ? ad.age_months + ' شهر' : null, icon: '⏰' },
    { label: 'الموقع', value: ad.city?.name_ar, icon: '📍' },
    { label: 'نوع السعر', value: priceTypeLabel, icon: '🏷' },
  ].filter(s => s.value);

  const renderComment = (comment: Comment, isReply?: boolean) => {
    const avatarColors = ['#1976D2','#F44336','#4CAF50','#7B1FA2','#FF9800','#00BCD4','#E91E63','#424242'];
    const colorIdx = (comment.user?.id || 0) % avatarColors.length;
    const avatarBg = comment.is_owner ? '#1976D2' : avatarColors[colorIdx];

    if (isReply) {
      return (
        <div key={comment.id} style={{ marginRight: 52, padding: '15px 20px', marginTop: 12, background: '#FAFAFA', borderRadius: 10, borderRight: '3px solid #BBDEFB' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13, overflow: 'hidden' }}>
                {comment.user?.avatar ? <img src={comment.user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (comment.user?.name?.charAt(0) || '؟')}
              </div>
              {comment.user?.is_verified && <div style={{ position: 'absolute', bottom: -1, left: -1, width: 14, height: 14, background: '#4CAF50', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: '#fff', border: '2px solid #fff' }}>✓</div>}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' as const }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#212121' }}>{comment.user?.name || 'مستخدم'}</span>
                {comment.is_owner && <span style={{ background: '#E3F2FD', color: '#1976D2', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8 }}>صاحب الإعلان</span>}
                {comment.user?.is_verified && <span style={{ fontSize: 10, fontWeight: 600, color: '#4CAF50' }}>✓ موثّق</span>}
                <span style={{ fontSize: 12, color: '#BDBDBD' }}>{timeAgo(comment.created_at)}</span>
              </div>
            </div>
          </div>
          <div style={{ paddingRight: 44, fontSize: 13, color: '#424242', lineHeight: 1.7, marginBottom: 8 }}>{comment.body}</div>
          <div style={{ paddingRight: 44, display: 'flex', gap: 15 }}>
            <button onClick={() => deleteComment(comment.id)} style={{ background: 'none', border: 'none', color: '#9E9E9E', fontSize: 12, cursor: 'pointer', fontFamily: "'Cairo', sans-serif", fontWeight: 600 }}>حذف</button>
          </div>
        </div>
      );
    }

    return (
      <div key={comment.id} style={{ padding: '20px 30px', borderBottom: '1px solid #F5F5F5' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 16, overflow: 'hidden' }}>
              {comment.user?.avatar ? <img src={comment.user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (comment.user?.name?.charAt(0) || '؟')}
            </div>
            {comment.user?.is_verified && <div style={{ position: 'absolute', bottom: -1, left: -1, width: 16, height: 16, background: '#4CAF50', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#fff', border: '2px solid #fff' }}>✓</div>}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' as const }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#212121' }}>{comment.user?.name || 'مستخدم'}</span>
              {comment.is_owner && <span style={{ background: '#E3F2FD', color: '#1976D2', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8 }}>صاحب الإعلان</span>}
              {comment.user?.is_verified && <span style={{ fontSize: 10, fontWeight: 600, color: '#4CAF50' }}>✓ موثّق</span>}
              <span style={{ fontSize: 12, color: '#BDBDBD' }}>{timeAgo(comment.created_at)}</span>
            </div>
          </div>
        </div>
        <div style={{ paddingRight: 52, fontSize: 14, color: '#424242', lineHeight: 1.7, marginBottom: 10 }}>{comment.body}</div>
        <div style={{ paddingRight: 52, display: 'flex', gap: 15 }}>
          <button onClick={() => setReplyTo({ id: comment.id, name: comment.user?.name || '' })} style={{ background: 'none', border: 'none', color: '#9E9E9E', fontSize: 12, cursor: 'pointer', fontFamily: "'Cairo', sans-serif", fontWeight: 600 }}>↩ رد</button>
          <button onClick={() => deleteComment(comment.id)} style={{ background: 'none', border: 'none', color: '#9E9E9E', fontSize: 12, cursor: 'pointer', fontFamily: "'Cairo', sans-serif", fontWeight: 600 }}>حذف</button>
        </div>
        {comment.replies && comment.replies.length > 0 && comment.replies.map(r => renderComment(r, true))}
      </div>
    );
  };

  return (
    <>
      <style>{`
        .adp-wrap{max-width:900px;margin:25px auto;padding:0 20px;font-family:'Cairo',sans-serif}
        .adp-card{background:#fff;border-radius:12px;border:1px solid #E0E0E0;overflow:hidden;margin-bottom:20px}
        .adp-header{background:linear-gradient(135deg,#E3F2FD,#BBDEFB);padding:25px 30px;border-bottom:1px solid #BBDEFB}
        .adp-header-top{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:12px}
        .adp-title{font-size:18px;font-weight:700;color:#212121;margin:0}
        .adp-btn-report{display:flex;align-items:center;gap:5px;background:none;border:1px solid #BDBDBD;padding:6px 14px;border-radius:20px;font-family:'Cairo',sans-serif;font-size:11px;color:#9E9E9E;cursor:pointer;white-space:nowrap}
        .adp-btn-report:hover{border-color:#F44336;color:#F44336;background:rgba(244,67,54,0.05)}
        .adp-meta{display:flex;align-items:center;gap:20px;font-size:13px;color:#757575;flex-wrap:wrap}
        .adp-meta span{display:flex;align-items:center;gap:5px}
        .adp-meta-icon{color:#1976D2}
        .adp-seller{display:flex;align-items:center;justify-content:space-between;padding:18px 30px;border-bottom:1px solid #EEEEEE}
        .adp-seller-right{display:flex;align-items:center;gap:12px;text-decoration:none;color:inherit}
        .adp-avatar-wrap{position:relative;flex-shrink:0}
        .adp-avatar{width:48px;height:48px;border-radius:50%;background:#1976D2;display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:700;color:#fff;overflow:hidden}
        .adp-v-badge{position:absolute;bottom:-2px;left:-2px;width:20px;height:20px;background:#4CAF50;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;color:#fff;border:2px solid #fff}
        .adp-seller-name{font-size:15px;font-weight:700;color:#212121}
        .adp-v-text{display:inline-flex;align-items:center;gap:3px;background:#E8F5E9;color:#4CAF50;font-size:10px;font-weight:700;padding:2px 8px;border-radius:8px}
        .adp-seller-area{font-size:12px;color:#9E9E9E;margin-top:2px}
        .adp-seller-since{font-size:11px;color:#BDBDBD;margin-top:2px}
        .adp-btn-follow{background:#1976D2;color:#fff;border:none;padding:8px 22px;border-radius:20px;font-family:'Cairo',sans-serif;font-size:13px;font-weight:600;cursor:pointer}
        .adp-btn-follow:hover{background:#0D47A1}
        .adp-desc{padding:20px 30px;font-size:15px;color:#424242;line-height:1.8;border-bottom:1px solid #EEEEEE;white-space:pre-wrap}
        .adp-price{padding:20px 30px;display:flex;align-items:center;gap:15px;border-bottom:1px solid #EEEEEE}
        .adp-price-label{font-size:14px;color:#9E9E9E}
        .adp-price-val{font-size:16px;font-weight:500;color:#212121;font-family:'Inter','Cairo',sans-serif}
        .adp-price-val small{font-size:13px;font-weight:600;font-family:'Cairo',sans-serif}
        .adp-price-type{font-size:12px;color:#9E9E9E;margin-right:10px}
        .adp-specs{display:grid;grid-template-columns:repeat(3,1fr)}
        .adp-spec{padding:16px 12px;text-align:center;border-bottom:1px solid #EEEEEE;border-left:1px solid #EEEEEE}
        .adp-spec:hover{background:#FAFAFA}
        .adp-spec-icon{font-size:18px;margin-bottom:8px}
        .adp-spec-label{font-size:10px;color:#BDBDBD;margin-bottom:4px}
        .adp-spec-value{font-size:13px;font-weight:500;color:#212121}
        .adp-imgs{padding:20px 30px;border-top:1px solid #EEEEEE}
        .adp-img-main{width:100%;max-height:400px;object-fit:contain;border-radius:12px;cursor:pointer;background:#FAFAFA}
        .adp-img-thumbs{display:flex;gap:8px;margin-top:12px;overflow-x:auto;padding-bottom:4px}
        .adp-img-thumb{width:70px;height:70px;border-radius:8px;object-fit:cover;cursor:pointer;border:2px solid #E0E0E0}
        .adp-img-thumb-active{border-color:#1976D2}
        .adp-img-empty{width:100%;height:300px;background:linear-gradient(135deg,#E3F2FD,#BBDEFB);border-radius:12px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;color:#1976D2;border:2px dashed #BBDEFB}
        .adp-btns{padding:15px 30px 0;display:flex;gap:8px;flex-wrap:wrap}
        .adp-btns-row{display:contents}
        .adp-btn{padding:8px 12px;border:none;border-radius:8px;font-size:12px;font-weight:500;flex:1;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;font-family:'Cairo',sans-serif;transition:all 0.2s;text-decoration:none}
        .adp-btn-call{background:#4CAF50;color:#fff}.adp-btn-call:hover{background:#388E3C}
        .adp-btn-chat{background:#1976D2;color:#fff}.adp-btn-chat:hover{background:#0D47A1}
        .adp-btn-sec{background:#fff;color:#616161;border:1px solid #E0E0E0}.adp-btn-sec:hover{background:#FAFAFA}
        .adp-warn{padding:0 30px 25px;margin-top:20px}
        .adp-warn-box{background:#FFFDE7;border:1px solid #FFC107;border-radius:10px;padding:15px 20px;display:flex;align-items:center;gap:12px;font-size:13px;color:#F57F17;line-height:1.7}
        .adp-comments{background:#fff;border-radius:12px;border:1px solid #E0E0E0;overflow:hidden;margin-bottom:20px}
        .adp-comments-hdr{padding:20px 30px;border-bottom:1px solid #EEEEEE;display:flex;align-items:center;gap:10px}
        .adp-comments-hdr h3{font-size:18px;font-weight:700;color:#212121;margin:0}
        .adp-comments-count{background:#1976D2;color:#fff;font-size:12px;font-weight:700;padding:2px 10px;border-radius:12px}
        .adp-comment-input{padding:20px 30px;display:flex;gap:12px;align-items:flex-start;border-bottom:1px solid #EEEEEE;flex-direction:column}
        .adp-comment-input-row{display:flex;gap:12px;width:100%}
        .adp-input{flex:1;padding:12px 16px;border:1.5px solid #E0E0E0;border-radius:10px;font-family:'Cairo',sans-serif;font-size:14px;outline:none;background:#FAFAFA}
        .adp-input:focus{border-color:#1976D2;background:#fff}
        .adp-btn-send{background:#757575;color:#fff;border:none;padding:12px 24px;border-radius:10px;font-family:'Cairo',sans-serif;font-size:14px;font-weight:700;cursor:pointer;white-space:nowrap}
        .adp-btn-send:hover{background:#616161}.adp-btn-send:disabled{background:#BDBDBD}
        .adp-reply-bar{display:flex;align-items:center;gap:8px;background:#E3F2FD;padding:8px 14px;border-radius:8px;font-size:12px;color:#1976D2;width:100%;margin-bottom:8px}
        .adp-reply-bar button{background:none;border:none;color:#9E9E9E;cursor:pointer;margin-right:auto;font-size:14px}
        .adp-no-comments{padding:40px 30px;text-align:center;color:#BDBDBD;font-size:14px}
        .adp-similar-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:15px}
        .adp-similar-hdr h3{font-size:18px;font-weight:700;color:#212121;margin:0}
        .adp-similar-hdr a{font-size:13px;color:#1976D2;text-decoration:none;font-weight:600}
        .adp-similar-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:15px;margin-bottom:20px}
        .adp-similar-card{background:#fff;border-radius:12px;border:1px solid #E0E0E0;overflow:hidden;text-decoration:none;color:inherit;transition:all 0.2s}
        .adp-similar-card:hover{box-shadow:0 4px 15px rgba(0,0,0,0.08);transform:translateY(-2px)}
        .adp-similar-img{width:100%;height:140px;background:linear-gradient(135deg,#E3F2FD,#BBDEFB);display:flex;align-items:center;justify-content:center;overflow:hidden}
        .adp-similar-img img{width:100%;height:100%;object-fit:cover}
        .adp-similar-body{padding:14px 16px}
        .adp-similar-title{font-size:14px;font-weight:700;color:#212121;margin-bottom:6px}
        .adp-similar-meta{display:flex;align-items:center;justify-content:space-between}
        .adp-similar-price{font-size:16px;font-weight:800;color:#1976D2;font-family:'Inter','Cairo',sans-serif}
        .adp-similar-price small{font-size:11px;font-weight:500;font-family:'Cairo',sans-serif}
        .adp-similar-loc{font-size:11px;color:#9E9E9E}
        .adp-breadcrumb{font-size:13px;display:flex;align-items:center;gap:5px;flex-wrap:wrap;margin-bottom:15px;padding:5px 0}
        .adp-breadcrumb a{color:#1976D2;text-decoration:none}
        .adp-breadcrumb .sep{color:#BDBDBD;margin:0 2px}
        .adp-lightbox{position:fixed;inset:0;z-index:999;background:rgba(0,0,0,0.9);display:flex;align-items:center;justify-content:center}
        .adp-lightbox-close{position:absolute;top:15px;left:15px;color:rgba(255,255,255,0.7);font-size:30px;cursor:pointer;background:none;border:none;z-index:10}
        .adp-lightbox-img{max-width:90vw;max-height:85vh;object-fit:contain}
        .adp-lightbox-nav{position:absolute;top:50%;transform:translateY(-50%);color:rgba(255,255,255,0.5);font-size:40px;cursor:pointer;background:none;border:none;z-index:10}
        .adp-lightbox-nav:hover{color:#fff}
        @media(max-width:600px){.adp-specs{grid-template-columns:repeat(2,1fr)}.adp-title{font-size:20px}.adp-price-val{font-size:22px}.adp-similar-grid{grid-template-columns:1fr}.adp-header,.adp-seller,.adp-desc,.adp-price,.adp-imgs,.adp-btns,.adp-warn,.adp-comments-hdr,.adp-comment-input{padding-left:15px;padding-right:15px}}
      `}</style>

      <div className="adp-wrap">
        <div className="adp-breadcrumb">
          <Link to="/">الرئيسية</Link>
          <span className="sep">/</span>
          <Link to="/ads">الإعلانات</Link>
          {ad.category && (<><span className="sep">/</span><Link to={'/categories/' + ad.category.slug}>{ad.category.name_ar}</Link></>)}
          {ad.subcategory && (<><span className="sep">/</span><span style={{ color: '#757575' }}>{ad.subcategory.name_ar}</span></>)}
        </div>

        <div className="adp-card">
          <div className="adp-header">
            <div className="adp-header-top">
              <h1 className="adp-title">{ad.title}</h1>
              <button className="adp-btn-report">🚩 إبلاغ عن الإعلان</button>
            </div>
            <div className="adp-meta">
              {ad.city && <span><span className="adp-meta-icon">📍</span> {ad.city.name_ar}</span>}
              <span><span className="adp-meta-icon">🕐</span> {timeAgo(ad.published_at || ad.created_at)}</span>
              <span><span className="adp-meta-icon">👁</span> {ad.views_count || 0} مشاهدة</span>
            </div>
          </div>

          {ad.user && (
            <div className="adp-seller">
              <Link to={'/users/' + ad.user.id} className="adp-seller-right">
                <div className="adp-avatar-wrap">
                  <div className="adp-avatar">
                    {ad.user.avatar ? <img src={ad.user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ad.user.name.charAt(0)}
                  </div>
                  {ad.user.is_verified && <div className="adp-v-badge">✓</div>}
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="adp-seller-name">{ad.user.name}</span>
                    {ad.user.is_verified && <span className="adp-v-text">✓ موثّق</span>}
                    {ad.user.account_type === 'company' && <span style={{ background: '#E3F2FD', color: '#1976D2', fontSize: 10, padding: '2px 8px', borderRadius: 8, fontWeight: 700 }}>متجر</span>}
                  </div>
                  <div className="adp-seller-area">{ad.user.city}</div>
                  {ad.user.created_at && <div className="adp-seller-since">📅 {memberSince(ad.user.created_at)}</div>}
                </div>
              </Link>
              <button className="adp-btn-follow">+ متابعة</button>
            </div>
          )}

          <div className="adp-desc">{ad.description}</div>

          {ad.price && (
            <div className="adp-price">
              <span className="adp-price-label">السعر</span>
              <span className="adp-price-val">{Number(ad.price).toLocaleString()} <small>ر.س</small></span>
              {priceTypeLabel && <span className="adp-price-type">({priceTypeLabel})</span>}
            </div>
          )}

          <div className="adp-specs">
            {specs.map((spec, i) => (
              <div key={i} className="adp-spec" style={{
                borderLeft: (i + 1) % 3 === 0 ? 'none' : '1px solid #EEEEEE',
                borderBottom: i >= specs.length - (specs.length % 3 || 3) ? 'none' : '1px solid #EEEEEE'
              }}>
                <div className="adp-spec-icon">{spec.icon}</div>
                <div className="adp-spec-label">{spec.label}</div>
                <div className="adp-spec-value">{spec.value}</div>
              </div>
            ))}
          </div>

          <div className="adp-imgs">
            {ad.images && ad.images.length > 0 ? (
              <>
                <img src={ad.images[activeImage]?.image_url} alt={ad.title} className="adp-img-main" onClick={() => setImageZoom(true)} />
                {ad.images.length > 1 && (
                  <div className="adp-img-thumbs">
                    {ad.images.map((img, idx) => (
                      <img key={img.id} src={img.thumbnail_url || img.image_url} alt="" className={'adp-img-thumb' + (activeImage === idx ? ' adp-img-thumb-active' : '')} onClick={() => setActiveImage(idx)} />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="adp-img-empty">
                <span style={{ fontSize: 50 }}>📷</span>
                <span style={{ fontSize: 14, fontWeight: 600 }}>صورة الإعلان</span>
              </div>
            )}
          </div>

          <div className="adp-btns">
            <div className="adp-btns-row">
              {ad.contact_phone && (
                <button className="adp-btn adp-btn-call" onClick={() => { if (showPhone) { window.location.href = 'tel:' + ad.contact_phone; } else { setShowPhone(true); } }}>
                  📞 {showPhone ? ad.contact_phone : 'اتصال'}
                </button>
              )}
              {ad.user && (
                <Link to={'/my/conversations?ad_id=' + ad.id + '&user_id=' + ad.user.id} className="adp-btn adp-btn-chat">
                  💬 محادثة
                </Link>
              )}
            </div>
            <div className="adp-btns-row">
              <button className="adp-btn adp-btn-sec"><span style={{ color: '#F44336' }}>♡</span> مفضلة</button>
              <button className="adp-btn adp-btn-sec" onClick={() => { if (navigator.share) { navigator.share({ title: ad.title, url: window.location.href }); } else { navigator.clipboard.writeText(window.location.href); } }}>
                <span style={{ color: '#1976D2' }}>🔗</span> مشاركة
              </button>
            </div>
          </div>

          <div className="adp-warn">
            <div className="adp-warn-box">
              <span style={{ fontSize: 22, flexShrink: 0 }}>⚠️</span>
              <span>تأكد من معاينة المواشي قبل الشراء. منصة الجلب غير مسؤولة عن أي صفقات تتم بين البائع والمشتري.</span>
            </div>
          </div>
        </div>

        <div className="adp-comments">
          <div className="adp-comments-hdr">
            <h3>التعليقات والردود</h3>
            {commentsTotal > 0 && <span className="adp-comments-count">{commentsTotal}</span>}
          </div>
          <div className="adp-comment-input">
            {replyTo && (
              <div className="adp-reply-bar">
                <span>رد على {replyTo.name}</span>
                <button onClick={() => setReplyTo(null)}>✕</button>
              </div>
            )}
            <div className="adp-comment-input-row">
              <input type="text" className="adp-input" value={commentBody} onChange={(e) => setCommentBody(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submitComment()} placeholder={replyTo ? 'رد على ' + replyTo.name + '...' : 'اكتب تعليق أو سوم...'} maxLength={500} />
              <button className="adp-btn-send" onClick={submitComment} disabled={commentSubmitting || !commentBody.trim()}>{commentSubmitting ? '...' : 'إرسال'}</button>
            </div>
          </div>
          <div>
            {commentLoading ? (
              <div style={{ padding: '40px 30px', textAlign: 'center', color: '#BDBDBD' }}>جاري التحميل...</div>
            ) : comments.length > 0 ? (
              comments.map(c => renderComment(c))
            ) : (
              <div className="adp-no-comments">لا توجد تعليقات بعد</div>
            )}
          </div>
        </div>

        {similarAds.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div className="adp-similar-hdr">
              <h3>إعلانات مشابهة</h3>
              <Link to="/ads">عرض الكل ←</Link>
            </div>
            <div className="adp-similar-grid">
              {similarAds.slice(0, 3).map(sa => (
                <Link key={sa.id} to={'/ads/' + sa.id} className="adp-similar-card">
                  <div className="adp-similar-img">
                    {sa.images && sa.images.length > 0 ? <img src={sa.images[0].image_url} alt="" /> : <span style={{ fontSize: 40, color: '#1976D2' }}>🐑</span>}
                  </div>
                  <div className="adp-similar-body">
                    <div className="adp-similar-title">{sa.title}</div>
                    <div className="adp-similar-meta">
                      <span className="adp-similar-price">{Number(sa.price).toLocaleString()} <small>ر.س</small></span>
                      {sa.city && <span className="adp-similar-loc">📍 {sa.city.name_ar}</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {imageZoom && ad.images.length > 0 && (
        <div className="adp-lightbox" onClick={() => setImageZoom(false)}>
          <button className="adp-lightbox-close" onClick={() => setImageZoom(false)}>✕</button>
          {ad.images.length > 1 && (
            <>
              <button className="adp-lightbox-nav" style={{ right: 15 }} onClick={(e) => { e.stopPropagation(); setActiveImage(prev => (prev + 1) % ad.images.length); }}>‹</button>
              <button className="adp-lightbox-nav" style={{ left: 15 }} onClick={(e) => { e.stopPropagation(); setActiveImage(prev => (prev - 1 + ad.images.length) % ad.images.length); }}>›</button>
            </>
          )}
          <img src={ad.images[activeImage]?.image_url} alt={ad.title} className="adp-lightbox-img" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </>
  );
};

export default AdDetailPage;
